import { useEffect, useState } from "react";
import { Power, Download, ShieldAlert, Wrench, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";

export default function Owner() {
  const [s, setS] = useState(null);

  const load = () => api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!s) return <p className="text-white/40 font-mono text-sm">Loading…</p>;
  const bot = s.bot || {};

  const setBotStatus = async (status) => {
    if (status === "offline" && !window.confirm("EMERGENCY: take the bot offline everywhere?")) return;
    try {
      await api.post("/admin/owner/bot-status", { status });
      toast.success(`Bot status → ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleMaintenance = async () => {
    await api.post("/admin/owner/maintenance", { enabled: !s.maintenance_mode });
    toast.success(`Maintenance ${!s.maintenance_mode ? "enabled" : "disabled"}`);
    load();
  };

  const exportData = async () => {
    const res = await api.get("/admin/owner/export");
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `groovemusic-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    toast.success("Backup exported");
  };

  return (
    <div data-testid="admin-owner">
      <PageTitle title="Owner Panel" sub="Global controls. Every action here is audited." />
      <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="owner-bot-controls">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4 flex items-center gap-2"><Crown size={13} className="text-amber-400" /> Global bot controls</p>
          <div className="flex items-center justify-between mb-5 text-sm">
            <span className="text-white/50">Current status</span>
            <Badge tone={bot.status === "online" ? "green" : bot.status === "offline" ? "red" : "amber"}>{bot.status}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["online", "idle", "dnd"].map((st) => (
              <button key={st} onClick={() => setBotStatus(st)} data-testid={`owner-status-${st}`}
                className="border border-white/15 hover:border-emerald-400/50 hover:text-emerald-400 text-white/70 text-sm font-semibold py-2.5 rounded-md capitalize transition-colors duration-150">
                Set {st}
              </button>
            ))}
            <button onClick={() => setBotStatus("offline")} data-testid="owner-emergency-shutdown"
              className="inline-flex items-center justify-center gap-2 border border-red-400/40 bg-red-400/10 hover:bg-red-400/20 text-red-400 text-sm font-bold py-2.5 rounded-md transition-colors duration-150">
              <Power size={14} /> Emergency shutdown
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.03] p-6" data-testid="owner-maintenance">
          <p className="text-[11px] uppercase tracking-[0.18em] text-amber-400/80 font-semibold mb-4 flex items-center gap-2"><Wrench size={13} /> Maintenance mode</p>
          <p className="text-sm text-white/60 mb-5">Flags the whole platform as under maintenance on the public status page.</p>
          <div className="flex items-center justify-between">
            <Badge tone={s.maintenance_mode ? "amber" : "grey"}>{s.maintenance_mode ? "ACTIVE" : "inactive"}</Badge>
            <button onClick={toggleMaintenance} data-testid="owner-maintenance-toggle"
              className="border border-amber-400/40 hover:bg-amber-400/10 text-amber-300 text-sm font-semibold px-5 py-2.5 rounded-md transition-colors duration-150">
              {s.maintenance_mode ? "Disable" : "Enable"}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="owner-backup">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4 flex items-center gap-2"><Download size={13} className="text-volt" /> Backup & data</p>
          <p className="text-sm text-white/60 mb-5">Download the full content database (settings, commands, docs, reviews, changelog) as JSON.</p>
          <button onClick={exportData} data-testid="owner-export"
            className="inline-flex items-center gap-2 border border-volt/40 hover:bg-volt/10 text-volt text-sm font-semibold px-5 py-2.5 rounded-md transition-colors duration-150">
            <Download size={14} /> Export database
          </button>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="owner-security">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4 flex items-center gap-2"><ShieldAlert size={13} className="text-blurple" /> Security posture</p>
          <ul className="space-y-2.5 text-sm text-white/60">
            <li className="flex justify-between"><span>Brute-force lockout</span><Badge tone="green">5 tries / 15 min</Badge></li>
            <li className="flex justify-between"><span>Session tokens</span><Badge tone="green">JWT · 12h</Badge></li>
            <li className="flex justify-between"><span>Discord OAuth</span><Badge tone={bot.oauth ? "green" : "grey"}>env-configured</Badge></li>
            <li className="flex justify-between"><span>Action auditing</span><Badge tone="green">enabled</Badge></li>
            <li className="flex justify-between"><span>Role enforcement</span><Badge tone="green">server-side</Badge></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
